import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH_ROOT } from '@/routes/paths';
import {
  VideoCall as MeetingIcon,
  Chat as ChatIcon,
  CloudUpload as CloudIcon,
  DrawOutlined as SignIcon,
} from '@mui/icons-material';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Chit, ChitContent, ChitMain, ChitAvatar, ChitText, ChitBadge } from 'src/components/ui/chit';
import { DEF_JAM_ORG_ID } from '@/mockData/common/demoFilters';
import { useOrg } from 'src/contexts/OrgContext';
import { useDemoOrg } from 'src/contexts/DemoOrgContext';
import { useProfile } from 'src/hooks/profile/useProfile';

interface OrganizationIntegrationsProps {
  isDemo?: boolean;
}

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: string;
}

const OrganizationIntegrations: React.FC<OrganizationIntegrationsProps> = ({ isDemo = false }) => {
  const navigate = useNavigate();
  const { selectedOrgId } = useOrg();
  const demoOrgContext = useDemoOrg();
  const { profile } = useProfile();

  const orgId = isDemo
    ? (demoOrgContext?.selectedOrg?.orgId ?? DEF_JAM_ORG_ID)
    : (selectedOrgId || profile?.userId);

  const integrations: Integration[] = [
    {
      id: 'meeting-bot',
      name: 'Meeting Bot',
      description: 'AI meeting assistant that captures deal terms and action items from calls',
      icon: <MeetingIcon />,
      category: 'Meetings',
    },
    {
      id: 'slack',
      name: 'Slack',
      description: 'Receive deal updates and collaborate with your team in Slack',
      icon: <ChatIcon />,
      category: 'Messaging',
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      description: 'Track deal-related conversations from WhatsApp',
      icon: <ChatIcon />,
      category: 'Messaging',
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      description: 'Import contact information and track deal-related messages',
      icon: <ChatIcon />,
      category: 'Messaging',
    },
    {
      id: 'google-drive',
      name: 'Google Drive',
      description: 'Sync documents and contracts from Google Drive to your vault',
      icon: <CloudIcon />,
      category: 'Document Storage',
    },
    {
      id: 'dropbox',
      name: 'Dropbox',
      description: 'Import and sync documents from Dropbox',
      icon: <CloudIcon />,
      category: 'Document Storage',
    },
    {
      id: 'onedrive',
      name: 'OneDrive',
      description: 'Connect OneDrive for document sync and storage',
      icon: <CloudIcon />,
      category: 'Document Storage',
    },
    {
      id: 'docusign',
      name: 'DocuSign',
      description: 'Send and track documents for electronic signature',
      icon: <SignIcon />,
      category: 'E-Signature',
    },
    {
      id: 'adobe-sign',
      name: 'Adobe Sign',
      description: 'Electronic signature workflows through Adobe Sign',
      icon: <SignIcon />,
      category: 'E-Signature',
    },
  ];

  // Group by category
  const grouped = integrations.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, Integration[]>);

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizationManagement(orgId || '') : PATH_ROOT.organization.management)}
          className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-2xl font-semibold text-foreground">Integrations</h1>
        <p className="text-muted-foreground mt-1">Organization-wide integrations and connected tools</p>
      </div>

      <div className="mb-6 p-4 rounded-xl border border-border bg-card">
        <p className="text-muted-foreground text-sm">
          Personal integrations like email and calendar are managed in your <button
            onClick={() => navigate(PATH_ROOT.main.profile)}
            className="text-foreground/70 underline underline-offset-2 hover:text-foreground transition-colors bg-transparent border-none cursor-pointer p-0 text-sm"
          >profile settings</button>.
        </p>
      </div>

      {Object.entries(grouped).map(([category, items]) => (
        <div key={category} className="mb-8">
          <h2 className="text-foreground text-lg font-semibold mb-4 px-1">
            {category}
          </h2>

          <div className="space-y-3">
            {items.map((integration) => (
              <Chit key={integration.id} variant="default">
                <ChitContent>
                  <ChitMain>
                    <ChitAvatar icon={integration.icon} />
                    <ChitText
                      title={integration.name}
                      description={integration.description}
                    />
                  </ChitMain>
                  <ChitBadge variant="default" size="sm">
                    Coming Soon
                  </ChitBadge>
                </ChitContent>
              </Chit>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-8">
        <Button
          variant="ghost"
          size="lg"
          onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizationManagement(orgId || '') : PATH_ROOT.organization.management)}
        >
          Back to Management
        </Button>
      </div>
    </div>
  );
};

export default OrganizationIntegrations;
